// packages/web/src/pages/ContactPage.tsx
import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import HelpIcon from '@mui/icons-material/Help';
import BugReportIcon from '@mui/icons-material/BugReport';
import { Header } from '../components/Layout/Header';
import { Footer } from '../components/Layout/Footer';
import { LoginDialog } from '../components/Auth/LoginDialog';
import { useAuth } from '../hooks/useAuth';

const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: '#ffffff',
});

const HeroSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#fafafa',
  padding: theme.spacing(8, 0, 6, 0),
  textAlign: 'center',
  borderBottom: '1px solid #e5e5e5',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 0, 4, 0),
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '3rem',
  fontWeight: 700,
  lineHeight: 1.2,
  marginBottom: theme.spacing(2),
  color: '#1a1a1a',
  letterSpacing: '-0.02em',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  color: '#666',
  lineHeight: 1.7,
  fontWeight: 400,
  maxWidth: '700px',
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 0),
  },
}));

const ContactCard = styled(Card)(({ theme }) => ({
  height: '100%',
  border: '1px solid #e5e5e5',
  borderRadius: '16px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  padding: theme.spacing(3),
  backgroundColor: '#ffffff',
  textAlign: 'center',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
    transform: 'translateY(-4px)',
    borderColor: '#c4d600',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '50%',
  backgroundColor: '#f0f8e8',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  '& svg': {
    fontSize: 32,
    color: '#84b817',
  },
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 700,
  marginBottom: theme.spacing(1),
  color: '#1a1a1a',
  letterSpacing: '-0.01em',
}));

const CardDescription = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#666',
  lineHeight: 1.7,
  fontWeight: 400,
}));

const FormSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#fafafa',
  padding: theme.spacing(6, 4),
  borderRadius: '16px',
  border: '1px solid #e5e5e5',
  marginTop: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 3),
  },
}));

const FormTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 700,
  marginBottom: theme.spacing(1),
  color: '#1a1a1a',
  letterSpacing: '-0.01em',
  textAlign: 'center',
}));

const FormSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#666',
  marginBottom: theme.spacing(4),
  textAlign: 'center',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2.5),
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    '&:hover fieldset': {
      borderColor: '#84b817',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#84b817',
    },
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#c4d600',
  color: '#1a1a1a',
  fontSize: '1rem',
  fontWeight: 700,
  padding: theme.spacing(1.5, 4),
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(196, 214, 0, 0.25)',
  width: '100%',
  marginTop: theme.spacing(1),
  '&:hover': {
    backgroundColor: '#b3c200',
    boxShadow: '0 6px 16px rgba(196, 214, 0, 0.35)',
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.2s ease',
}));

const ContactPage: React.FC = () => {
  const { user, signOut } = useAuth();
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleLoginClick = () => {
    setLoginDialogOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement form submission
    console.log('Form submitted:', formData);
    alert('Thank you for your message! We\'ll get back to you soon.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const contactOptions = [
    {
      icon: <EmailIcon />,
      title: 'General Inquiries',
      description: 'Have a question about Sched Tennis? We\'re here to help with any general questions.',
    },
    {
      icon: <HelpIcon />,
      title: 'Support',
      description: 'Need help with your account or having trouble using a feature? Let us know.',
    },
    {
      icon: <BugReportIcon />,
      title: 'Report a Bug',
      description: 'Found a bug or technical issue? Help us improve by reporting it.',
    },
  ];

  return (
    <PageContainer>
      <Header user={user} onSignOut={signOut} onLoginClick={handleLoginClick} />

      <HeroSection>
        <Container maxWidth="md">
          <PageTitle>Get in Touch</PageTitle>
          <PageSubtitle>
            We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </PageSubtitle>
        </Container>
      </HeroSection>

      <ContentSection>
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ mb: 6 }}>
            {contactOptions.map((option, index) => (
              <Grid item xs={12} md={4} key={index}>
                <ContactCard>
                  <CardContent>
                    <IconWrapper>
                      {option.icon}
                    </IconWrapper>
                    <CardTitle>{option.title}</CardTitle>
                    <CardDescription>{option.description}</CardDescription>
                  </CardContent>
                </ContactCard>
              </Grid>
            ))}
          </Grid>

          <Container maxWidth="md">
            <FormSection>
              <FormTitle>Send Us a Message</FormTitle>
              <FormSubtitle>
                Fill out the form below and we'll get back to you within 24-48 hours
              </FormSubtitle>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      fullWidth
                      label="Your Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      fullWidth
                      label="Your Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      label="Message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      multiline
                      rows={6}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SubmitButton type="submit">
                      Send Message
                    </SubmitButton>
                  </Grid>
                </Grid>
              </form>
            </FormSection>
          </Container>
        </Container>
      </ContentSection>

      <Footer />

      <LoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
      />
    </PageContainer>
  );
};

export default ContactPage;
